import { useState } from 'react';
import {
  Chip,
  Popover,
  Box,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Router,
  RouterOutlined,
  Sync as SyncIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { useMqttStore } from '../../stores/mqttStore';
import { useMqttDiagnostics } from '@api/bff/mqttDiagnostics';
import * as mqttService from '../../services/mqttService';

const MqttStatusIndicator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isConnected, brokerUrl, stationId, reconnectAttempts, lastError } =
    useMqttStore();
  const { backendMqttConnected, reconnect: reconnectBackend } =
    useMqttDiagnostics();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [browserReconnecting, setBrowserReconnecting] = useState(false);

  const handleReconnectBrowser = () => {
    setBrowserReconnecting(true);
    mqttService.reconnect();
    // Give it a moment to attempt connection
    setTimeout(() => setBrowserReconnecting(false), 3000);
  };

  const handleReconnectBackend = () => {
    reconnectBackend.mutate();
  };

  const StatusRow = ({
    label,
    connected
  }: {
    label: string;
    connected: boolean | null;
  }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircleIcon
        sx={{
          fontSize: 10,
          color:
            connected === null
              ? 'grey.400'
              : connected
                ? 'success.main'
                : 'error.main'
        }}
      />
      <Typography variant="body2">
        {label}:{' '}
        <strong>
          {connected === null
            ? 'Unknown'
            : connected
              ? 'Connected'
              : 'Disconnected'}
        </strong>
      </Typography>
    </Box>
  );

  return (
    <>
      <Chip
        icon={isConnected ? <Router /> : <RouterOutlined />}
        label={!isMobile ? 'MQTT' : undefined}
        color={isConnected ? 'success' : 'error'}
        variant="outlined"
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          height: isMobile ? 28 : 32,
          px: isMobile ? 0.5 : 1.5,
          minWidth: isMobile ? 28 : 'auto',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: isMobile ? '50%' : 2,
          borderWidth: 1.5,
          transition: 'all 0.2s ease-in-out',
          '& .MuiChip-label': {
            px: isMobile ? 0 : 0.5,
            py: 0.25
          },
          '& .MuiChip-icon': {
            fontSize: isMobile ? '14px' : '16px',
            ml: isMobile ? 0 : 0.5,
            mr: isMobile ? 0 : -0.25
          }
        }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 300, mt: 1 } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          MQTT Diagnostics
        </Typography>

        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}
        >
          <StatusRow label="Browser" connected={isConnected} />
          <StatusRow label="Server" connected={backendMqttConnected} />
        </Box>

        {brokerUrl && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Broker: {brokerUrl}
          </Typography>
        )}
        {stationId && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Station: {stationId}
          </Typography>
        )}
        {!isConnected && reconnectAttempts > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Reconnect attempts: {reconnectAttempts}
          </Typography>
        )}
        {lastError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Error: {lastError}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<SyncIcon />}
            onClick={handleReconnectBrowser}
            disabled={browserReconnecting}
          >
            {browserReconnecting ? 'Reconnecting...' : 'Reconnect Browser MQTT'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<SyncIcon />}
            onClick={handleReconnectBackend}
            disabled={reconnectBackend.isPending}
          >
            {reconnectBackend.isPending
              ? 'Reconnecting...'
              : 'Reconnect Server MQTT'}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default MqttStatusIndicator;
